import { cn } from '@/lib/utils'
import { Input } from './input'
import { Label } from './label'

const PRESET_COLORS = [
  { hex: '#E53935', name: 'Red' },
  { hex: '#1E88E5', name: 'Blue' },
  { hex: '#43A047', name: 'Green' },
  { hex: '#FDD835', name: 'Yellow' },
  { hex: '#8E24AA', name: 'Purple' },
  { hex: '#FB8C00', name: 'Orange' },
  { hex: '#00ACC1', name: 'Teal' },
  { hex: '#D81B60', name: 'Pink' },
  { hex: '#6D4C41', name: 'Brown' },
  { hex: '#757575', name: 'Gray' },
  { hex: '#212121', name: 'Black' },
] as const

interface ColorPickerProps {
  value: string
  onChange: (color: string) => void
  className?: string
}

export function ColorPicker({ value, onChange, className }: ColorPickerProps) {
  const isValidHex = /^#[0-9A-Fa-f]{6}$/.test(value)

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex flex-wrap gap-2">
        {PRESET_COLORS.map((color) => (
          <button
            key={color.hex}
            type="button"
            onClick={() => onChange(color.hex)}
            className={cn(
              'w-8 h-8 rounded-md border-2 transition-all',
              'hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary',
              value === color.hex
                ? 'border-foreground ring-2 ring-offset-2 ring-primary'
                : 'border-transparent'
            )}
            style={{ backgroundColor: color.hex }}
            title={color.name}
            aria-label={`Select ${color.name}`}
            aria-pressed={value === color.hex}
          />
        ))}
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Label htmlFor="custom-color" className="text-sm text-muted-foreground">
            Custom:
          </Label>
          <Input
            id="custom-color"
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            placeholder="#1E88E5"
            className="w-24 font-mono text-sm"
            maxLength={7}
          />
        </div>

        <div
          className={cn(
            'w-10 h-10 rounded-md border-2 border-border',
            !isValidHex && 'bg-muted'
          )}
          style={{ backgroundColor: isValidHex ? value : undefined }}
          aria-label={`Preview: ${isValidHex ? value : 'Invalid color'}`}
        />

        {!isValidHex && value.length > 0 && (
          <span className="text-sm text-destructive">Invalid hex color</span>
        )}
      </div>
    </div>
  )
}
